/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import debounce from 'lodash.debounce';

import { useToast } from 'context/ToastContext';
import { createTimeEntry, updateTimeEntry, getUserHourRate } from 'services/timerServices';
import { toastConstant } from 'constants/toastmessage';
import { convertToTimezoneWithStartOfDayFormatWithCurrentTime, convertToUserTimezoneDateOnly } from 'utils/utility_functions/timezone';
import { UserDetailsContext } from 'context/userDetailsContext';
import { listCasesAndLeads } from 'services/CaseServices';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import CaseListDropDown from 'components/CaseListDropDown/CaseListDropDown';
import { Calendar } from 'primereact/calendar';
import { generateFullName } from 'utils/generateFullNameUtils';
import { getUserId } from 'utils/utils';
import { useFirmUserList } from 'hooks/useFirmUserList';
import useActivityTypes from 'hooks/useActivityTypes';

export const CreateTimer = (props) => {
  const [selectedCase, setSelectedCase] = useState();
  const [description, setDescription] = useState();
  const [rate, setRate] = useState('');
  const [markAsNonBillable, setMarkAsNonBillable] = useState(false);
  const [caseList, setCaseList] = useState([]);
  const [selectedActivityType, setSelectedActivityType] = useState();
  const isFromTimeManger = props?.isFromTimeManger ?? false;
  const [duration, setDuration] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState();
  let [formError, setFormError] = useState({
    case: '',
    duration: '',
    rate: '',
    timeEntryDate: '',
  });
  const [timeEntryDate, setTimeEntryDate] = useState(new Date());
  const [userList, setUserList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const current_user_id = getUserId();
  const isEditMode = Boolean(props?.selectedTimer || props?.header?.includes('Edit'));

  const { addToast } = useToast();

  const userContext = useContext(UserDetailsContext);
  const { userList: userData } = useFirmUserList();
  const shouldSkipInitialLoad = Boolean(props.caseView || props.selectedTimer);
  const activityTypesFilters = props?.is_automated ? { status: 'active' } : { category: 'manual', status: 'active' };
  const { activityTypes, loadActivityTypes } = useActivityTypes(activityTypesFilters, shouldSkipInitialLoad);

  const loadedPracticeAreaRef = useRef(null);
  const loadedCaseIdRef = useRef(null);
  const hasInitializedFromTimer = useRef(false);
  const hasSetActivityType = useRef(false);

  const prepareAndSetUserList = async () => {
    try {
      // Format and enrich user list; append (Me) for current user
      const formattedList = userData.map((item) => {
        const fullName = generateFullName(item);
        const isMe = item?.user_id === current_user_id;
        return {
          ...item,
          assignee_name: isMe ? `${fullName} (Me)` : fullName,
        };
      });

      // Sort the formatted list alphabetically by first_name
      const sortedList = formattedList.sort((a, b) => {
        const nameA = (a?.first_name || '').toLowerCase();
        const nameB = (b?.first_name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setUserList(sortedList);

      // Determine selected user
      const selectedUser = isEditMode
        ? sortedList.find((user) => user?.firm_user_id === props?.selectedTimer?.created_by_details?.user_id)
        : sortedList.find((user) => user?.user_id === current_user_id);

      setSelectedUser(selectedUser || {});
    } catch (error) {
      console.error('Failed to fetch user list:', error);
    }
  };

  useEffect(() => {
    if (userData?.length > 0) {
      prepareAndSetUserList();
    } else {
      setUserList([]);
    }
  }, [userData]);

  function getAllCaseList(is_lead = false, case_id = '', exclude_pi_case = false, keyword = '', limit = 15, is_filter_dropdown = true) {
    listCasesAndLeads(is_lead, case_id, exclude_pi_case, keyword, limit, is_filter_dropdown)
      .then((res) => {
        let tempArray = [];
        res?.data?.case_list?.forEach((item) => {
          tempArray.push({
            label:
              item && item.case_description && item.case_description.case_name
                ? `${item.unique_number ? `${item.unique_number} ` : ''}${item.case_description.case_name}`
                : 'TBD',
            value: item.case_description.case_name,
            id: item?.case_id,
            practice_area: item?.case_description?.case_practice_area,
          });
        });
        setCaseList(tempArray);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  const setHoursRate = (case_id, team_member_firm_user_id) => {
    getUserHourRate(case_id, team_member_firm_user_id).then((response) => {
      if (typeof Number(response?.data?.rate) === 'number') {
        setRate(response?.data?.rate);
      }
    });
  };

  useEffect(() => {
    if (selectedCase?.id && selectedUser?.firm_user_id && !isEditMode) {
      setHoursRate(selectedCase?.id, selectedUser?.firm_user_id);
    }
  }, [selectedCase, selectedUser, isEditMode]);

  useEffect(() => {
    if (!selectedCase?.id) return;

    if (props?.is_automated) {
      loadActivityTypes(null);
      return;
    }
    const currentCaseId = selectedCase?.id;
    const currentPracticeArea = selectedCase?.practice_area?.trim() || null;

    if (loadedCaseIdRef.current === currentCaseId && loadedPracticeAreaRef.current === currentPracticeArea) {
      return;
    }

    if (currentPracticeArea && currentPracticeArea !== '') {
      const practiceAreaIds = [currentPracticeArea];
      loadActivityTypes(practiceAreaIds);
    } else {
      loadActivityTypes(null);
    }

    loadedCaseIdRef.current = currentCaseId;
    loadedPracticeAreaRef.current = currentPracticeArea;
  }, [selectedCase?.id, selectedCase?.practice_area, loadActivityTypes]);

  useEffect(() => {
    if (selectedActivityType && activityTypes && activityTypes.length > 0) {
      const isBillingCodeValid = activityTypes.some((activity) => activity.activity_type_id === selectedActivityType.activity_type_id);
      if (!isBillingCodeValid) {
        setSelectedActivityType(null);
      }
    }
  }, [activityTypes]);

  useEffect(() => {
    if (props.selectedTimer) {
      setDuration(props.selectedTimer?.total_hours);
      setMarkAsNonBillable(!props.selectedTimer.billable);
      setDescription(props.selectedTimer.notes);

      const dateString = props?.initialTimeEntryDate || props?.selectedTimer?.time_entry_date;

      if (dateString) {
        const userTimezone = userContext?.userDetails?.timezone;

        const dateObject = convertToUserTimezoneDateOnly(dateString, userTimezone);

        setTimeEntryDate(dateObject);
      }

      if (props.selectedTimer.hour_rate) {
        setRate(props.selectedTimer.hour_rate);
      }
    }
  }, [props.selectedTimer, props.caseView, userContext?.userDetails?.timezone]);

  useEffect(() => {
    if (props.selectedTimer && !hasInitializedFromTimer.current && !props.caseView) {
      let case_id = props.selectedTimer.case_id;
      let case_label = props?.selectedTimer?.case_name;
      let activity_type_id = props.selectedTimer.activity_type_id;

      let practice_area = null;
      if (caseList && caseList.length > 0) {
        const matchingCase = caseList?.find((case_item) => case_item?.id === case_id);
        if (matchingCase) {
          practice_area = matchingCase?.practice_area;
        }
      }
      const caseData = {
        ...props?.selectedTimer,
        id: case_id,
        label: case_label,
        practice_area: practice_area || props?.selectedTimer?.case_practice_area,
      };

      setSelectedCase(caseData);
      hasInitializedFromTimer.current = true;

      if (activityTypes && activityTypes?.length > 0 && !hasSetActivityType.current) {
        let selectedActivity = activityTypes.filter((index) => index.activity_type_id === activity_type_id);
        if (selectedActivity[0]) {
          setSelectedActivityType({ ...selectedActivity[0] });
          hasSetActivityType.current = true;
        }
      }
    }
  }, [activityTypes, caseList, props.selectedTimer, props.caseView]);

  useEffect(() => {
    if (props.selectedTimer && activityTypes && activityTypes?.length > 0 && !hasSetActivityType.current) {
      const activity_type_id = props.selectedTimer?.activity_type_id;
      const selectedActivity = activityTypes?.find((index) => index.activity_type_id === activity_type_id);
      if (selectedActivity) {
        setSelectedActivityType(selectedActivity);
        hasSetActivityType.current = true;
      }
    }
  }, [activityTypes, props.selectedTimer]);

  useEffect(() => {
    if (props.selectedTimer && hasInitializedFromTimer.current && caseList && caseList?.length > 0 && !selectedCase?.practice_area) {
      const matchingCase = caseList?.find((case_item) => case_item?.id === props.selectedTimer?.case_id);
      if (matchingCase?.practice_area) {
        setSelectedCase((prev) => ({ ...prev, practice_area: matchingCase.practice_area }));
      }
    }
  }, [caseList, props.selectedTimer]);

  useEffect(() => {
    if (props.caseView) {
      let case_id = props?.caseDetails?.case_id;
      let case_label = props?.caseDetails?.case_description?.case_name;
      setSelectedCase({
        id: case_id,
        label: case_label,
        ...props?.caseDetails,
        practice_area: props?.caseDetails?.case_description?.case_practice_area,
      });
    }
  }, [props.caseView, props.caseDetails, props.selectedTimer, activityTypes]);

  const fetchData = useCallback((query) => {
    getAllCaseList(false, '', false, query, 15, true);
  }, []);

  const delayedSearch = useCallback(
    debounce((query) => {
      fetchData(query);
    }, 500),
    [fetchData]
  );

  useEffect(() => {
    if (searchValue !== '') {
      delayedSearch(searchValue);
    } else if (searchValue === '') {
      getAllCaseList(false, '', false, searchValue, 15, true);
    }
  }, [searchValue, delayedSearch]);

  const searchCase = (e) => {
    let query = e?.query;
    setSearchValue(query);
  };

  const onChangeCase = (e) => {
    setSelectedCase(e);
    formError.case = '';
    setFormError({ ...formError });
  };

  const submitTimeEntry = () => {
    if (validateForm()) {
      setLoading(true);
      let data = {
        start_time: new Date().getTime(),
        case_id: props.isLeadSpecific ? props?.caseDetails?.case_id : props?.caseView ? props?.caseDetails?.case_id : selectedCase?.id,
        case_name: props?.isLeadSpecific
          ? props?.caseDetails?.case_description?.case_name
          : props?.caseView
          ? props?.caseDetails?.case_description?.case_name
          : !props?.caseView && !props?.isLeadSpecific
          ? selectedCase?.case_name
          : selectedCase?.value,
        billable: !markAsNonBillable,
        notes: description,
        hour_rate: `${rate}`,
        time_entry_status: 'OPEN',
        activity_type: selectedActivityType?.activity_type,
        activity_type_id: selectedActivityType?.activity_type_id,
        activity_type_code: selectedActivityType?.activity_type_code,
        total_hours: String(Number(duration)),
        amount: `${duration * rate}`,

        team_member_cognito_id: selectedUser?.client_cognito_username || '',
        team_member_firm_user_id: selectedUser?.firm_user_id || '',
        team_member_id: selectedUser?.user_id || '',
        team_member_name: selectedUser?.assignee_name || '',
      };

      if (props.selectedTimer) {
        let request_data = { ...props.selectedTimer, ...data };
        request_data.time_entry_status = 'OPEN';
        if (!Boolean(props?.is_automated)) {
          if (props?.isDateChanged) {
            request_data.time_entry_date = convertToTimezoneWithStartOfDayFormatWithCurrentTime(
              timeEntryDate,
              userContext?.userDetails?.timezone
            );
          } else {
            // Format initialTimeEntryDate with proper timezone
            const dateToFormat = props?.initialTimeEntryDate ? new Date(props.initialTimeEntryDate) : new Date();
            request_data.time_entry_date = convertToTimezoneWithStartOfDayFormatWithCurrentTime(
              dateToFormat,
              userContext?.userDetails?.timezone
            );
          }
        }
        updateTimeEntry(request_data, props.isLeadSpecific ? props.isLeadSpecific : selectedCase?.is_lead, '', true, true)
          .then((response) => {
            addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.UPDATE_TIME_ENTRY_SUCCESS);
            setLoading(false);
            props?.setIsDateChanged(false);
            typeof props.createTimerSuccess === 'function' && props.createTimerSuccess();
          })
          .catch((err) => {
            setLoading(false);
            addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.UPDATE_TIME_ENTRY_FAILED);
          });
      } else {
        let date = new Date();
        if (props?.is_automated) {
          data.time_entry_date = convertToTimezoneWithStartOfDayFormatWithCurrentTime(date, userContext?.userDetails?.timezone);
        } else {
          data.time_entry_date = convertToTimezoneWithStartOfDayFormatWithCurrentTime(timeEntryDate, userContext?.userDetails?.timezone);
        }
        const entry = { ...data };
        delete entry.time_entry_status;
        const payload = { time_entry_status: 'OPEN', time_entries: [entry] };
        createTimeEntry(payload, props.isLeadSpecific ? props?.isLeadSpecific : selectedCase?.is_lead)
          .then((response) => {
            addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.CREATE_TIME_ENTRY_SUCCESS);
            setLoading(false);
            props?.setIsDateChanged(false);
            typeof props.createTimerSuccess === 'function' && props.createTimerSuccess();
          })
          .catch((err) => {
            console.log(err);
            setLoading(false);
            addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.CREATE_TIME_ENTRY_FAILED);
          });
      }
    }
  };

  const validateForm = () => {
    let valid = true;
    if (!selectedCase) {
      valid = false;
      formError.case = 'Required';
    }
    if (!duration) {
      valid = false;
      formError.duration = 'Required';
    }
    if (duration && isNaN(duration)) {
      valid = false;
      formError.duration = 'Invalid';
    }
    if (!markAsNonBillable && (!rate || (rate && rate <= 0))) {
      valid = false;
      formError.rate = 'Required a valid rate';
    }
    if (!timeEntryDate) {
      valid = false;
      formError.timeEntryDate = 'Required';
    }

    setFormError({ ...formError });
    return valid;
  };

  return (
    <>
      <div className="row">
        {!isFromTimeManger && <div className="col-12 mb-3"> {props.selectedTimer ? 'Update Time Entry' : 'Add Time Entry'}</div>}
        {isFromTimeManger && (
          <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
            <div className="modal-heading F-size16 text-bold">{props.header}</div>
          </div>
        )}
      </div>
      <div className="row p-2">
        <div className="col-12 col-sm-4 mb-3 d-flex align-items-center">
          <label className="p-medium">
            Duration
            <span className="text-danger">*</span>
          </label>
        </div>
        <div className="col-12 col-sm-8 mb-3">
          <InputText
            className="input-shadow w-100 p-2 p-d-block"
            value={duration}
            onChange={(e) => {
              let value = e.target.value;
              // Restrict to only numeric and at most one decimal with one digit
              if (value === '' || /^\d+(\.\d{0,1})?$/.test(value)) {
                setDuration(value);
                formError.duration = '';
                setFormError({ ...formError });
              }
            }}
            inputMode="decimal"
            pattern="^\d+(\.\d{0,1})?$"
          />
          <span id="username1-help" className="p-d-block pt-2">
            0.1 = 6 minutes
          </span>
          {formError && formError.duration && <span className="text-danger d-block">{formError.duration}</span>}
        </div>
        {!Boolean(props?.is_automated) && (
          <>
            <div className="col-12 col-sm-4 mb-3 d-flex align-items-center">
              <label className="p-medium">Date</label>
              <span className="text-danger">*</span>
            </div>
            <div className="col-12 col-sm-8 mb-3">
              <Calendar
                value={timeEntryDate}
                onChange={(e) => {
                  setTimeEntryDate(e.value);
                  props?.setIsDateChanged(true);
                  formError.timeEntryDate = '';
                }}
                // minDate={new Date()}
                placeholder="Select Date"
                className="input-shadow w-100"
              />
              {formError && formError.timeEntryDate && <span className="text-danger d-block">{formError.timeEntryDate}</span>}
            </div>
          </>
        )}

        {!Boolean(props.caseView) && (
          <>
            <div className="col-12 col-sm-4 mb-3 d-flex align-items-center">
              <label className="p-medium">Case/Lead</label>
              <span className="text-danger">*</span>
            </div>
            <div className="col-12 col-sm-8 mb-3">
              <CaseListDropDown
                value={selectedCase}
                onChange={(e) => {
                  onChangeCase(e);
                }}
                placeholder="Case/Lead Name"
                noOptionsMessage="No Cases/Leads Found"
                loadOptions={searchCase}
                defaultOptions={caseList}
              />
              {formError && formError.case && <span className="text-danger">Required</span>}
            </div>
          </>
        )}

        <div className="col-12 col-md-4 mb-3 d-flex align-items-center">
          <label className="p-medium">Billing Code</label>
        </div>
        <div className="col-12 col-md-8 mb-3">
          <Dropdown
            options={activityTypes}
            value={selectedActivityType}
            optionLabel={'activity_label'}
            onChange={(e) => {
              setSelectedActivityType(e.value);
            }}
            className="w-100"
            filter
            filterBy="activity_type,activity_type_code"
            filterPlaceholder="Search"
            placeholder="Select Billing Code"
            disabled={isEditMode && props.is_automated}
          />
        </div>

        <div className="col-12 col-md-4 mb-3 d-flex align-items-center">
          <label className="p-medium">Billed For</label>
        </div>
        <div className="col-12 col-md-8 mb-3">
          <Dropdown
            options={userList}
            value={selectedUser}
            optionLabel={'assignee_name'}
            onChange={(e) => {
              setSelectedUser(e.value);
            }}
            checkmark={true}
            highlightOnSelect={false}
            className="w-100"
            filter
            filterBy="assignee_name"
            filterPlaceholder="Search User"
            placeholder="Billing User"
            disabled={isEditMode && props.is_automated}
          />
        </div>

        <div className="col-12 col-sm-4 mb-3">
          <label className="p-medium">Description</label>
        </div>
        <div className="col-12 col-sm-8 mb-3">
          <TextSnippetWrapper
            type="textarea"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
            className="input-shadow w-100"
            autoComplete="off"
          />
        </div>

        <div className="col-12 col-sm-4 mb-3 d-flex align-items-center">
          <label className="p-medium">
            Hourly Rate
            <span className="text-danger">{!markAsNonBillable ? '*' : ''}</span>
          </label>
        </div>
        <div className="col-12 col-sm-8 mb-3">
          <InputNumber
            id="rate"
            name="rate"
            placeholder="Rate"
            className="input-shadow w-100"
            value={rate}
            onChange={(e) => {
              if (e.value === null) {
                setRate(0);
              } else {
                setRate(e.value);
              }
              formError.rate = '';
              setFormError({ ...formError });
            }}
            mode="currency"
            currency="USD"
            locale="en-US"
            min={0}
          />
          {formError && formError.rate && <span className="text-danger">{formError.rate}</span>}
        </div>

        <div className="col-12 col-sm-4 mb-3 d-flex align-items-center">
          <label className="p-medium">Total Amount</label>
        </div>
        <div className="col-12 col-sm-8 mb-3">
          <InputNumber
            id="total_amount"
            placeholder="Duration * Rate"
            className="input-shadow w-100"
            value={!isNaN(rate * duration) ? rate * duration : ''}
            mode="currency"
            currency="USD"
            locale="en-US"
            min={0}
            disabled={true}
          />
          {formError && formError.rate && <span className="text-danger">{formError.rate}</span>}
        </div>

        <div className="col-12 my-3 d-flex align-items-center">
          <Checkbox
            checked={markAsNonBillable}
            onChange={(e) => {
              setMarkAsNonBillable(e.target.checked);
              if (e.target.checked) {
                formError.rate = '';
                setFormError({ ...formError });
              }
            }}
          />
          <label className="ms-2 p-medium">Mark as non-billable</label>
        </div>
        <div className="footer-btn">
          <Button className="p-button-secondary outline mx-2" label="Cancel" onClick={props.cancelCreateTimer}></Button>
          <Button
            className="p-button p-button-secondary"
            label={props.selectedTimer ? 'Update' : 'Save'}
            onClick={submitTimeEntry}
            loading={loading}
          ></Button>
        </div>
      </div>
    </>
  );
};
